import React, { useState, useEffect, useCallback } from "react";
import { Plus, Edit2, Trash2, Search, X, Loader, RefreshCw, Save, Globe, ExternalLink, Image as ImageIcon } from "lucide-react";
import { brandsAPI, uploadAPI } from "../../services/api";
import { getImageUrl } from "../../utils/image";

function BrandUploadBtn({ onUploaded }) {
  const [uploading, setUploading] = useState(false);
  const handleChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const { data } = await uploadAPI.single(file, "brands");
      onUploaded(data.url);
    } catch {
      alert("Logo upload failed");
    } finally {
      setUploading(false);
    }
  };
  return (
    <label className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border border-gray-200 text-xs font-semibold cursor-pointer transition-colors ${uploading ? "opacity-50 pointer-events-none" : "hover:bg-brand-pale hover:border-brand-primary text-gray-600"}`}>
      {uploading ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <ImageIcon className="w-3.5 h-3.5" />}
      {uploading ? "Uploading..." : "Upload Logo"}
      <input type="file" accept="image/*" className="hidden" onChange={handleChange} disabled={uploading} />
    </label>
  );
}

const emptyBrand = { name: "", logoUrl: "", websiteUrl: "", description: "", order: 0, isActive: true };

export default function AdminBrands() {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editBrand, setEditBrand] = useState(null);
  const [form, setForm] = useState(emptyBrand);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");

  const fetchBrands = useCallback(async () => {
    setLoading(true);
    try {
      const res = await brandsAPI.getAllAdmin();
      setBrands(res.data.brands || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBrands();
  }, [fetchBrands]);

  const openAdd = () => {
    setEditBrand(null);
    setForm({ ...emptyBrand, order: brands.length });
    setShowModal(true);
  };

  const openEdit = (b) => {
    setEditBrand(b);
    setForm({ ...emptyBrand, ...b });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      alert("Brand name is required");
      return;
    }
    if (!form.logoUrl.trim()) {
      alert("Brand logo is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        logoUrl: form.logoUrl.trim(),
        websiteUrl: form.websiteUrl ? form.websiteUrl.trim() : null,
        description: form.description ? form.description.trim() : null,
        order: parseInt(form.order, 10) || 0,
        isActive: form.isActive !== false
      };

      if (editBrand) {
        await brandsAPI.update(editBrand.id, payload);
      } else {
        await brandsAPI.create(payload);
      }
      setShowModal(false);
      fetchBrands();
    } catch (err) {
      alert(err.response?.data?.error || "Failed to save brand");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete brand "${name}"?`)) return;
    try {
      await brandsAPI.delete(id);
      fetchBrands();
    } catch (err) {
      alert(err.response?.data?.error || "Delete failed");
    }
  };

  const handleToggleActive = async (b) => {
    try {
      await brandsAPI.update(b.id, { isActive: !b.isActive });
      fetchBrands();
    } catch (err) {
      alert("Failed to update status");
    }
  };

  const filtered = brands.filter(b => !search || b.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-heading font-bold text-gray-900 text-xl">Our Brands</h2>
          <p className="text-gray-400 text-sm">{brands.length} brand partners displayed on homepage</p>
        </div>
        <div className="flex gap-2">
          <button onClick={fetchBrands} className="w-9 h-9 flex items-center justify-center bg-white border border-gray-200 rounded-xl hover:bg-gray-50 shadow-soft">
            <RefreshCw className="w-4 h-4 text-gray-500" />
          </button>
          <button onClick={openAdd} className="btn-primary text-xs px-4 py-2.5">
            <Plus className="w-4 h-4" /> Add Brand
          </button>
        </div>
      </div>

      <div className="relative max-w-xs">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input className="input-field pl-9 py-2 text-sm" placeholder="Search brands..." value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader className="w-6 h-6 text-brand-primary animate-spin mr-2" />
          <span className="text-gray-400 text-sm">Loading brands...</span>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filtered.map(b => (
            <div key={b.id} className="bg-white rounded-2xl p-5 shadow-card border border-gray-100 flex flex-col justify-between hover:shadow-hover transition-all">
              <div>
                <div className="h-28 bg-gray-50 rounded-xl flex items-center justify-center p-4 mb-4 border border-gray-100">
                  <img src={getImageUrl(b.logoUrl)} alt={b.name} className="max-h-full max-w-full object-contain" onError={e => { e.target.src = "/logo-dark.png"; }} />
                </div>
                <div className="flex items-start justify-between gap-2 mb-1">
                  <h3 className="font-heading font-bold text-gray-900 text-base">{b.name}</h3>
                  <button onClick={() => handleToggleActive(b)} className={`text-[11px] font-bold px-2 py-0.5 rounded-full transition-colors ${b.isActive ? "bg-green-100 text-green-700 hover:bg-green-200" : "bg-gray-100 text-gray-500 hover:bg-gray-200"}`}>
                    {b.isActive ? "Active" : "Inactive"}
                  </button>
                </div>
                {b.description && <p className="text-xs text-gray-500 line-clamp-2 mb-2">{b.description}</p>}
                <div className="text-xs text-gray-400 flex items-center gap-2">
                  <span>Order: {b.order}</span>
                  {b.websiteUrl && (
                    <a href={b.websiteUrl} target="_blank" rel="noopener noreferrer" className="text-brand-primary flex items-center gap-1 hover:underline">
                      Website <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </div>
              <div className="flex gap-2 mt-4 pt-3 border-t border-gray-100">
                <button onClick={() => openEdit(b)} className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl bg-brand-pale text-brand-primary text-xs font-semibold hover:bg-brand-muted transition-colors">
                  <Edit2 className="w-3.5 h-3.5" /> Edit
                </button>
                <button onClick={() => handleDelete(b.id, b.name)} className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl bg-red-50 text-red-500 text-xs font-semibold hover:bg-red-100 transition-colors">
                  <Trash2 className="w-3.5 h-3.5" /> Delete
                </button>
              </div>
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="col-span-full text-center py-16 text-gray-400 bg-white rounded-2xl shadow-card">
              <Globe className="w-12 h-12 mx-auto mb-3 text-gray-200" />
              No brands found
            </div>
          )}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4 py-8 overflow-y-auto">
          <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full my-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h3 className="font-heading font-bold text-gray-900 text-lg">{editBrand ? "Edit Brand" : "Add Brand"}</h3>
              <button onClick={() => setShowModal(false)} className="w-8 h-8 rounded-xl bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-gray-200">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Brand Name *</label>
                <input className="input-field text-sm" placeholder="e.g. Sava Choice" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Brand Logo *</label>
                <div className="flex gap-2">
                  <input className="input-field text-sm flex-1" placeholder="Logo image URL..." value={form.logoUrl} onChange={e => setForm(f => ({ ...f, logoUrl: e.target.value }))} />
                  <BrandUploadBtn onUploaded={url => setForm(f => ({ ...f, logoUrl: url }))} />
                </div>
                {form.logoUrl && (
                  <div className="mt-2 p-3 bg-gray-50 rounded-xl inline-block border border-gray-100">
                    <img src={getImageUrl(form.logoUrl)} alt="Preview" className="h-16 w-auto object-contain" onError={e => { e.target.style.display = "none"; }} />
                  </div>
                )}
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Website URL (Optional)</label>
                <input className="input-field text-sm" placeholder="https://..." value={form.websiteUrl} onChange={e => setForm(f => ({ ...f, websiteUrl: e.target.value }))} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Description (Optional)</label>
                <textarea className="input-field resize-none text-sm" rows={2} placeholder="Brief note about the brand or product line..." value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Display Order</label>
                  <input type="number" className="input-field text-sm" value={form.order} onChange={e => setForm(f => ({ ...f, order: e.target.value }))} />
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <input type="checkbox" id="brandActive" checked={form.isActive} onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))} className="w-4 h-4 accent-brand-primary" />
                  <label htmlFor="brandActive" className="text-sm font-medium text-gray-700">Active (Visible)</label>
                </div>
              </div>
            </div>
            <div className="p-5 border-t border-gray-100 flex gap-3">
              <button onClick={() => setShowModal(false)} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 text-sm font-medium">Cancel</button>
              <button onClick={handleSave} disabled={saving} className="flex-1 btn-primary justify-center py-2.5 text-sm">
                {saving ? <><Loader className="w-4 h-4 animate-spin" /> Saving...</> : <><Save className="w-4 h-4" /> {editBrand ? "Update" : "Add"} Brand</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
